/*
 * <cart-drawer> — opt-in AJAX cart drawer.
 * Active only when settings.cart_type === 'drawer' (the section renders the element).
 *
 * Open/close reuses the theme's existing modal plumbing (showInPageModal /
 * closeThemeModal / ESC / click-outside / scroll-lock). Content stays server-rendered:
 * every mutation re-renders sections/cart-drawer.liquid via the Section Rendering API and
 * this component swaps only [data-cart-drawer-panel]'s contents. Cross-sell is fetched
 * separately from the product recommendations endpoint.
 *
 * See docs/adr/0002-cart-drawer-architecture.md.
 */
(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // The cart count bubble lives inside #site-control; scope to it so unrelated
  // ".cart" elements elsewhere on the page are never mutated.
  function headerCartLinks() {
    return document.querySelectorAll('#site-control .cart:not(.nav-search)');
  }

  // Cart AJAX endpoints must end in ".js" to return JSON (the bare /cart/change
  // form endpoint 302-redirects to the HTML cart page instead).
  function withJs(url) {
    return /\.js$/.test(url) ? url : url + '.js';
  }

  var CartDrawer = class extends HTMLElement {
    constructor() {
      super();
      this._recReq = 0;
      this._busy = false;
      this._openingFresh = false;
      this._lastFocus = null;
      this._keydownTrap = null;
    }

    connectedCallback() {
      this.panel = this.querySelector('[data-cart-drawer-panel]');
      this.changeUrl = withJs(this.dataset.cartChangeUrl || '/cart/change');
      this.addUrl = withJs(this.dataset.cartAddUrl || '/cart/add');
      this.updateUrl = withJs(this.dataset.cartUpdateUrl || '/cart/update');

      // Hook for the legacy add-to-cart flow (feedback-drawer branch in theme.js).
      window.theme = window.theme || {};
      window.theme.cartDrawer = this;

      this._onClick = this._onClick.bind(this);
      this._onChange = this._onChange.bind(this);
      this.addEventListener('click', this._onClick);
      this.addEventListener('change', this._onChange);

      // Record the trigger for focus-restore when the drawer is opened from the
      // header cart icon (capture phase, before the theme's toggle handler runs).
      this._onToggleClick = (e) => {
        var t = e.target.closest('[data-modal-toggle="#cart-drawer"]');
        if (t) this._lastFocus = t;
      };
      document.addEventListener('click', this._onToggleClick, true);

      // Mirror the theme's .reveal class to fire open/close lifecycle hooks for
      // every path (icon, ESC, click-outside, programmatic).
      this._wasOpen = this.classList.contains('reveal');
      this._observer = new MutationObserver(() => {
        var isOpen = this.classList.contains('reveal');
        if (isOpen && !this._wasOpen) this._onOpen();
        else if (!isOpen && this._wasOpen) this._onClose();
        this._wasOpen = isOpen;
      });
      this._observer.observe(this, { attributes: true, attributeFilter: ['class'] });
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('change', this._onChange);
      document.removeEventListener('click', this._onToggleClick, true);
      this._releaseFocus();
    }

    /* ---------------------------------------------------------------- events */

    _onClick(e) {
      var closeEl = e.target.closest('[data-cart-drawer-close]');
      if (closeEl && this.contains(closeEl)) {
        e.preventDefault();
        this.close();
        return;
      }

      var qtyBtn = e.target.closest('[data-cart-drawer-qty]');
      if (qtyBtn) {
        e.preventDefault();
        var item = qtyBtn.closest('[data-cart-drawer-item]');
        if (!item) return;
        var input = item.querySelector('[data-cart-drawer-qty-input]');
        var current = parseInt(input.value, 10) || 0;
        var next = qtyBtn.getAttribute('data-cart-drawer-qty') === 'up' ? current + 1 : current - 1;
        if (next < 0) next = 0;
        this.changeLine(parseInt(item.getAttribute('data-line'), 10), next);
        return;
      }

      var removeBtn = e.target.closest('[data-cart-drawer-remove]');
      if (removeBtn) {
        e.preventDefault();
        var rItem = removeBtn.closest('[data-cart-drawer-item]');
        if (!rItem) return;
        this.changeLine(parseInt(rItem.getAttribute('data-line'), 10), 0);
        return;
      }

      var addBtn = e.target.closest('button[data-cart-drawer-add]');
      if (addBtn) {
        e.preventDefault();
        var vid = addBtn.getAttribute('data-variant-id');
        if (vid) this.addVariant(vid, 1);
        return;
      }
    }

    _onChange(e) {
      var input = e.target.closest('[data-cart-drawer-qty-input]');
      if (!input) return;
      var item = input.closest('[data-cart-drawer-item]');
      if (!item) return;
      var qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 0) qty = 0;
      this.changeLine(parseInt(item.getAttribute('data-line'), 10), qty);
    }

    /* ------------------------------------------------------ open / close hooks */

    _onOpen() {
      this._trapFocus();
      // Add-to-cart already refreshed before opening (_openingFresh); the header
      // cart-icon path has not, so re-sync from the server on open to avoid stale data.
      if (this._openingFresh) {
        this._openingFresh = false;
        return;
      }
      this.refresh();
    }

    _onClose() {
      this._releaseFocus();
      if (this._lastFocus && typeof this._lastFocus.focus === 'function') {
        try { this._lastFocus.focus(); } catch (e) {}
      }
    }

    open() {
      // Close any other open modal (e.g. quick-buy) so modals never stack.
      var other = document.querySelector('.theme-modal.reveal');
      if (other && other !== this && typeof window.closeThemeModal === 'function') {
        window.closeThemeModal(true);
      }
      if (document.activeElement && document.activeElement !== document.body) {
        this._lastFocus = document.activeElement;
      }
      if (window.jQuery && typeof window.showInPageModal === 'function') {
        window.showInPageModal(window.jQuery(this));
      } else {
        this.classList.remove('unreveal');
        this.classList.add('reveal');
        document.body.classList.add('modal-active');
      }
    }

    close() {
      if (typeof window.closeThemeModal === 'function') {
        window.closeThemeModal();
      } else {
        this.classList.remove('reveal');
        document.body.classList.remove('modal-active');
      }
    }

    /* ------------------------------------------------------------ focus trap */

    _trapFocus() {
      this._releaseFocus();
      this._keydownTrap = (e) => {
        if (e.key !== 'Tab' && e.keyCode !== 9) return;
        var nodes = Array.prototype.filter.call(
          this.panel.querySelectorAll(FOCUSABLE),
          function (el) { return el.offsetParent !== null; }
        );
        if (!nodes.length) return;
        var first = nodes[0];
        var last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };
      this.addEventListener('keydown', this._keydownTrap);
      var closeBtn = this.querySelector('[data-cart-drawer-close]');
      if (closeBtn) { try { closeBtn.focus(); } catch (e) {} }
    }

    _releaseFocus() {
      if (this._keydownTrap) {
        this.removeEventListener('keydown', this._keydownTrap);
        this._keydownTrap = null;
      }
    }

    /* -------------------------------------------------------------- mutations */

    setBusy(state) {
      this._busy = state;
      this.classList.toggle('cart-drawer--loading', state);
      this.setAttribute('aria-busy', state ? 'true' : 'false');
    }

    changeLine(line, quantity) {
      if (this._busy || !line) return Promise.resolve();
      this.setBusy(true);
      var body = {
        line: line,
        quantity: quantity,
        sections: SECTION_ID,
        sections_url: window.location.pathname
      };
      return fetch(this.changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json(); })
        .then((data) => {
          if (data && data.sections && data.sections[SECTION_ID]) {
            this.swapPanel(data.sections[SECTION_ID]);
            this.afterRender();
            return Promise.resolve();
          }
          return this.refresh();
        })
        .catch(() => this.refresh())
        .then(() => { this.setBusy(false); });
    }

    addVariant(id, quantity) {
      if (this._busy) return Promise.resolve();
      this.setBusy(true);
      return fetch(this.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: id, quantity: quantity || 1 }] })
      })
        .then(() => this.refresh())
        .catch(() => {})
        .then(() => { this.setBusy(false); });
    }

    /* Re-render the section and swap the panel.
     * Primary: a no-op POST to /cart/update.js with the `sections` param — this
     * renders the drawer even though it lives in layout/theme.liquid (the bare
     * ?sections= GET does NOT render layout-only sections). GET kept as fallback. */
    refresh() {
      return fetch(this.updateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ sections: SECTION_ID, sections_url: window.location.pathname })
      })
        .then(function (r) { return r.json(); })
        .then((data) => {
          var html = data && data.sections && data.sections[SECTION_ID];
          if (html) {
            this.swapPanel(html);
            this.afterRender();
            return true;
          }
          return false;
        })
        .then((ok) => { if (!ok) return this._refreshViaGet(); })
        .catch(() => this._refreshViaGet());
    }

    _refreshViaGet() {
      var url = new URL(window.location.href);
      url.searchParams.set('sections', SECTION_ID);
      return fetch(url.toString(), { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then((data) => {
          if (data && data[SECTION_ID]) {
            this.swapPanel(data[SECTION_ID]);
            this.afterRender();
          }
        })
        .catch(function () {});
    }

    renderAndOpen() {
      this._openingFresh = true;
      return this.refresh().then(() => { this.open(); });
    }

    /* Replace panel contents + sync root data attributes from fresh markup. */
    swapPanel(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var newRoot = doc.querySelector('cart-drawer');
      var newPanel = doc.querySelector('[data-cart-drawer-panel]');
      if (!newPanel || !this.panel) return;
      this.panel.innerHTML = newPanel.innerHTML;
      if (newRoot) {
        this.dataset.lastProductId = newRoot.dataset.lastProductId || '';
        this.dataset.cartItemCount = newRoot.dataset.cartItemCount || '0';
      }
    }

    afterRender() {
      this.updateHeaderCount(parseInt(this.dataset.cartItemCount, 10) || 0);
      // Re-init dynamic checkout (Shop Pay / PayPal) buttons after the swap.
      if (window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') {
        try { window.Shopify.PaymentButton.init(); } catch (e) {}
      }
      this.loadRecommendations();
    }

    /* ------------------------------------------------------------- header sync */

    updateHeaderCount(count) {
      headerCartLinks().forEach(function (link) {
        var bubble = link.querySelector(':scope > div');
        if (bubble) bubble.textContent = count;
        var text = link.querySelector('.text-link');
        if (text) {
          var base = text.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
          text.textContent = count > 0 ? base + ' (' + count + ')' : base;
        }
      });
    }

    /* ------------------------------------------------------------- cross-sell */

    loadRecommendations() {
      var wrap = this.querySelector('[data-cart-drawer-recommendations]');
      var list = this.querySelector('[data-cart-drawer-recommendations-list]');
      if (!wrap || !list) return;

      if (this.dataset.recommendationsEnabled !== 'true') { wrap.hidden = true; return; }
      var pid = this.dataset.lastProductId;
      if (!pid) { wrap.hidden = true; return; }

      var base = this.dataset.recommendationsUrl;
      var sectionId = this.dataset.recommendationsSection;
      var limit = this.dataset.recommendationsLimit || 4;
      var token = ++this._recReq;

      var build = (intent) =>
        base + '?section_id=' + encodeURIComponent(sectionId) +
        '&product_id=' + encodeURIComponent(pid) +
        '&limit=' + encodeURIComponent(limit) +
        '&intent=' + intent;

      var fetchHtml = (url) => fetch(url).then(function (r) { return r.text(); });

      fetchHtml(build('complementary'))
        .then((html) => {
          if (token !== this._recReq) return null;
          if (this._countItems(html) > 0) return html;
          return fetchHtml(build('related')); // fallback
        })
        .then((html) => {
          if (html === null || token !== this._recReq) return;
          if (this._countItems(html) > 0) {
            list.innerHTML = html;
            wrap.hidden = false;
          } else {
            list.innerHTML = '';
            wrap.hidden = true;
          }
        })
        .catch(function () { wrap.hidden = true; });
    }

    _countItems(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelectorAll('.cart-drawer-rec').length;
    }
  };

  if (!window.customElements.get('cart-drawer')) {
    window.customElements.define('cart-drawer', CartDrawer);
  }
})();
